import _ from 'lodash';
import Heading from '../../../components/heading';
import { updateTags } from '../../../api/cameras';
import translationKeys from '../../../translations/keys';
import { roundToHundred } from '../../../utils/maths.new';

export const getScheduleControl = (cameras, scheduleControlKey) => {
    return !!scheduleControlKey &&
        cameras.some(c => c.schedules?.some(s => s.category === scheduleControlKey));
};

export const isValidAccess = (permissions, access, isManager = false, isDemo = false) => 
    (isDemo && _.some(permissions, p => p !== 's')) || 
    isManager || 
    _.difference(access, permissions).length === 0;

export const getSettingLabelText = (setting, t) => {
    let label = typeof setting.text === 'string' 
        ? t(setting.text) 
        : t(...setting.text);
    
    if (setting.addon) {
        label = `${label} (${t(translationKeys.technical.analytics.addons.ADDON)})`;
    }
    
    return label;
};

export const MainHeading = (props) => (
    <Heading variant="5" style={{margin: '10px 0'}}>
        {props.children}
    </Heading>
);

// onChange function for adding and removing TagsInput
export const tagsInputOnChange = (cameras, setCameras, selectedCameras, addingTags, tagsInputState, setTagsInputState) => 
    async ({ target: { value }}) => {
        // Subset of `cameras` with just selected tags
        const selectedCamerasState = _.pickBy(cameras, (info, uidd) => selectedCameras.includes(uidd));

        // Tags added/removed since last onChange
        const tagsAdded = addingTags ? _.difference(value, tagsInputState) : _.difference(tagsInputState, value);
        const tagsRemoved = addingTags ? _.difference(tagsInputState, value) : _.difference(value, tagsInputState);

        // Find the added/removed tag
        const changedTag = tagsAdded[0] ?? tagsRemoved[0];
        // Is this a new tag entered or are we undoing a previous selected (by clicking X on tag)?
        const undo = (addingTags && tagsRemoved.length > 0) || (!addingTags && tagsAdded.length > 0);
        
        // State to return to in event of failure
        const currentTags = _.mapValues(selectedCamerasState, 'tags');

        // Get mapping of selected camera uidd to new tags
        const newTags = _.mapValues(currentTags, (tags, uidd) => {
            if (addingTags) {
                if (undo) {
                    // Only remove the tag if the camera didn't already have this tag when modal opened
                    // Otherwise that isn't undoing an "Add tag" action, but removing a tag
                    return tags;
                } else {
                    // Add tag
                    return _.union(tags, [changedTag]);
                }
            } else {
                if (undo) {
                    return tags;
                } else {
                    // Remove tag
                    return _.difference(tags, [changedTag]);
                }
            }
        });

        // Helper function to update state
        const updateState = newTags => {
            setCameras(cameras => {
                const newCameras = {...cameras};
                selectedCameras.forEach(uidd => {
                    newCameras[uidd].tags = newTags[uidd];
                });
                return newCameras;
            });
        }

        try {
            // Update state immediately so change appears instant to user
            updateState(newTags);
            setTagsInputState(value);
            // Make request
            await updateTags(newTags);
        } catch (error) {
            // If error, reset state
            updateState(currentTags);
            setTagsInputState(tagsInputState);
        }
    };

const baseBR = {
    h264: 400,
    h265: 300,
}
export const calculateTargetBitrate = (camera) => {
    const { vcodec, bitrate_multiplier } = camera;
    const codecValue = vcodec === 'h265' ? 'h265' : 'h264';
    return roundToHundred(baseBR[codecValue] * (bitrate_multiplier || 1)) + " kbps";
};
